import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import {
  School,
  Users,
  FileText,
  Heart,
  TrendingUp,
  Award,
  Calendar,
  Bell,
  LogOut,
  GraduationCap,
} from "lucide-react";
import { useNavigate } from "react-router-dom"; // @ts-ignore - react-router-dom is not a valid module

const Dashboard = () => {
  const navigate = useNavigate();

  const stats = [
    {
      title: "Total Schools",
      value: "2,547",
      change: "+12 this month",
      icon: School,
      color: "text-primary",
    },
    {
      title: "Active Students",
      value: "11,234",
      change: "+456 this week",
      icon: Users,
      color: "text-secondary",
    },
    {
      title: "Completed Exams",
      value: "3,421",
      change: "85% completion rate",
      icon: FileText,
      color: "text-accent",
    },
    {
      title: "Total Donations",
      value: "₹12.4L",
      change: "+₹45K this month",
      icon: Heart,
      color: "text-primary",
    },
  ];

  const recentActivities = [
    {
      title: "New school onboarded",
      school: "St. Mary's High School",
      time: "2 hours ago",
    },
    {
      title: "Exam completed",
      school: "Delhi Public School",
      time: "4 hours ago",
    },
    {
      title: "Activity submitted",
      school: "Modern High School",
      time: "1 day ago",
    },
    {
      title: "Donation received",
      school: "Anonymous Donor",
      time: "2 days ago",
    },
  ];

  const upcomingEvents = [
    {
      title: "Q1 Examination",
      date: "15 Jan 2024",
      participants: "8,500 students",
    },
    {
      title: "Monthly Activity Report",
      date: "20 Jan 2024",
      participants: "All schools",
    },
    {
      title: "Award Ceremony",
      date: "30 Jan 2024",
      participants: "Top 100 students",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-hero">
                <GraduationCap className="h-6 w-6 text-primary-foreground" />
              </div>
              <div>
                <span className="text-xl font-bold text-foreground">
                  Karuna Platform
                </span>
                <p className="text-xs text-muted-foreground">Admin Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button className="border-none bg-transparent hover:bg-transparent">
                <Bell className="h-5 w-5" />
              </Button>
              <Button
                className="border-none bg-transparent hover:bg-transparent"
                onClick={() => navigate("/")}
              >
                <LogOut className="h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-foreground">
            Welcome back, Administrator
          </h1>
          <p className="text-muted-foreground">
            Here&apos;s what&apos;s happening with your platform today
          </p>
        </div>

        {/* Stats Grid */}
        <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {stats.map(stat => {
            const Icon = stat.icon;
            return (
              <Card
                key={stat.title}
                className="border-border bg-gradient-card transition-all hover:shadow-medium"
              >
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {stat.title}
                  </CardTitle>
                  <Icon className={`h-5 w-5 ${stat.color}`} />
                </CardHeader>
                <CardContent>
                  <div className="mb-1 text-3xl font-bold text-foreground">
                    {stat.value}
                  </div>
                  <p className="flex items-center gap-1 text-xs text-muted-foreground">
                    <TrendingUp className="h-3 w-3" />
                    {stat.change}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Two Column Layout */}
        <div className="mb-8 grid gap-6 lg:grid-cols-2">
          {/* Recent Activities */}
          <Card className="border-border bg-gradient-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Recent Activities
              </CardTitle>
              <CardDescription>
                Latest updates from your schools
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentActivities.map(activity => (
                  <div
                    key={`${activity.title}-${activity.school}-${activity.time}`}
                    className="flex items-start gap-3 border-b border-border pb-4 last:border-0 last:pb-0"
                  >
                    <div className="mt-2 h-2 w-2 rounded-full bg-primary" />
                    <div className="flex-1">
                      <p className="font-medium text-foreground">
                        {activity.title}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {activity.school}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {activity.time}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Upcoming Events */}
          <Card className="border-border bg-gradient-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-secondary" />
                Upcoming Events
              </CardTitle>
              <CardDescription>
                Scheduled activities and deadlines
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {upcomingEvents.map(event => (
                  <div
                    key={`${event.title}-${event.date}-${event.participants}`}
                    className="flex items-start gap-3 border-b border-border pb-4 last:border-0 last:pb-0"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary/10">
                      <Award className="h-5 w-5 text-secondary" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-foreground">
                        {event.title}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {event.participants}
                      </p>
                    </div>
                    <span className="text-sm font-medium text-foreground">
                      {event.date}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="border-border bg-gradient-card">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Frequently used features</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Button className="h-auto flex-col gap-2 py-6">
                <School className="h-6 w-6 text-primary" />
                <span className="text-sm">Manage Schools</span>
              </Button>
              <Button className="h-auto flex-col gap-2 py-6">
                <Users className="h-6 w-6 text-secondary" />
                <span className="text-sm">Student Registry</span>
              </Button>
              <Button className="h-auto flex-col gap-2 py-6">
                <FileText className="h-6 w-6 text-accent" />
                <span className="text-sm">Create Exam</span>
              </Button>
              <Button className="h-auto flex-col gap-2 py-6">
                <Heart className="h-6 w-6 text-primary" />
                <span className="text-sm">Donations</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
